<script setup lang="ts">
import { PhChatCircleDots, PhHeadphones, PhImageSquare, PhPlay } from '@phosphor-icons/vue'
import { computed, type Component } from 'vue'
import { useRouter } from 'vue-router'
import { buildCartoonUrl } from '@/utils/distribute/navigation'
import { formatNumber } from '@/utils/number'
import { optimizeAvatarUrl } from '@/utils/distribute/media-url'
import {
  getSummaryContentAspectRatio,
  getSummaryContentTypeLabel,
  resolveSummaryContentKind,
} from '@/utils/profile/content-card'
import DreamaProgressiveImage from '@/components/dreama-progressive-image'
import type { Props } from './type'

defineOptions({
  name: 'SummaryContentCard',
})

interface RoleItem {
  id: string
  name: string
  avatar: string
  summary: string | null
  type?: string | null
}

const props = withDefaults(defineProps<Props>(), {
  priority: false,
})

const emit = defineEmits<{
  click: [item: Props['item']]
  authorClick: [item: Props['item']]
}>()
const router = useRouter()

const displayKind = computed(() => resolveSummaryContentKind(props.item))
const aspectRatio = computed(() => getSummaryContentAspectRatio(props.item))
const roleItems = computed<RoleItem[]>(() => {
  const relCartoons = (props.item.relCartoons ?? []).filter((item) => item?.avatar)

  if (relCartoons.length) {
    return relCartoons.slice(0, 3)
  }

  if (props.item.cartoonId && props.item.cartoonName && props.item.cartoonAvatar) {
    return [
      {
        id: props.item.cartoonId,
        name: props.item.cartoonName,
        avatar: props.item.cartoonAvatar,
        summary: null,
        type: null,
      },
    ]
  }

  return []
})
const hasRoleItems = computed(() => roleItems.value.length > 0)
const typeLabel = computed(() => getSummaryContentTypeLabel(props.item))
const annotationText = computed(() =>
  props.item.useNum > 0 ? formatNumber(props.item.useNum) : '',
)
const annotationIcon = computed<Component>(() => {
  if (displayKind.value === 'pipe-room') {
    return PhChatCircleDots
  }

  if (displayKind.value === 'radio') {
    return PhHeadphones
  }

  if (displayKind.value === 'media-imgtxt') {
    return PhImageSquare
  }

  if (displayKind.value === 'pipe-none') {
    return PhChatCircleDots
  }

  return PhPlay
})
const showAnnotationText = computed(() => Boolean(annotationText.value))
const stats = computed(() => {
  const items: string[] = []

  if (typeof props.item.likeNum === 'number' && props.item.likeNum > 0) {
    items.push(`${formatNumber(props.item.likeNum)}喜欢`)
  }

  if (typeof props.item.commentCount === 'number' && props.item.commentCount > 0) {
    items.push(`${formatNumber(props.item.commentCount)}评论`)
  }

  return items
})
const showStats = computed(() => stats.value.length > 0)
const cardLabel = computed(() => [props.item.name, typeLabel.value].filter(Boolean).join('，'))

function handleCardClick() {
  emit('click', props.item)
}

function handleAuthorClick() {
  if (!props.item.user) {
    return
  }

  emit('authorClick', props.item)
}

function handleRoleClick(role: RoleItem | null | undefined) {
  const url = buildCartoonUrl(role)

  if (!url) {
    return
  }

  void router.push(url)
}
</script>

<template>
  <article
    class="summary-card"
    :aria-label="cardLabel"
    role="button"
    tabindex="0"
    @click="handleCardClick"
    @keydown.enter.prevent="handleCardClick"
    @keydown.space.prevent="handleCardClick"
  >
    <DreamaProgressiveImage
      class="summary-card__cover"
      :class="`is-${displayKind}`"
      :src="item.cover"
      :alt="item.name"
      :aspect-ratio="aspectRatio"
      :priority="priority"
      :preview-width="48"
    >
      <div class="summary-card__shade" />

      <div v-if="hasRoleItems" class="summary-card__roles">
        <button
          v-for="role in roleItems"
          :key="role.id"
          type="button"
          class="summary-card__role-avatar"
          :title="role.name"
          @click.stop="handleRoleClick(role)"
        >
          <img :src="optimizeAvatarUrl(role.avatar) ?? role.avatar" :alt="role.name" />
        </button>
      </div>

      <div class="summary-card__badge" :class="{ 'has-text': showAnnotationText }">
        <component :is="annotationIcon" :size="20" weight="regular" />
        <span v-if="showAnnotationText">{{ annotationText }}</span>
      </div>
    </DreamaProgressiveImage>

    <div class="summary-card__body">
      <h3 class="summary-card__title">{{ item.name || '未命名作品' }}</h3>

      <button
        v-if="item.user"
        type="button"
        class="summary-card__author"
        @click.stop="handleAuthorClick"
      >
        <img
          class="summary-card__author-avatar"
          :src="optimizeAvatarUrl(item.user.avatar) ?? item.user.avatar"
          :alt="item.user.nickname"
        />
        <span>@{{ item.user.nickname }}</span>
      </button>

      <div v-if="showStats" class="summary-card__stats" aria-label="作品数据">
        <span v-for="stat in stats" :key="stat">{{ stat }}</span>
      </div>
    </div>
  </article>
</template>

<style scoped>
.summary-card {
  position: relative;
  display: inline-flex;
  width: 100%;
  break-inside: avoid;
  contain: layout paint style;
  content-visibility: auto;
  contain-intrinsic-size: auto 420px;
  cursor: pointer;
  flex-direction: column;
  padding: 12px 12px 16px;
  color: var(--profile-card-text);
  outline: none;
}

.summary-card::before {
  position: absolute;
  inset: 4px;
  z-index: -1;
  border-radius: 20px;
  background-color: var(--app-state-layer-hover);
  content: '';
  opacity: 0;
  transform: scale(var(--app-interaction-media-rest-scale));
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}

.summary-card:hover,
.summary-card:focus-visible {
  background: transparent;
}

.summary-card:hover::before,
.summary-card:focus-visible::before {
  opacity: 1;
  transform: scale(var(--app-interaction-media-reveal-scale));
}

.summary-card__cover {
  position: relative;
  width: 100%;
  overflow: hidden;
  border-radius: 12px;
  background: var(--app-skeleton-base);
}

.summary-card__shade {
  position: absolute;
  inset: auto 0 0;
  height: 48%;
  background: linear-gradient(180deg, rgb(0 0 0 / 0) 0%, rgb(0 0 0 / 0.5) 100%);
  pointer-events: none;
}

.summary-card__roles {
  position: absolute;
  bottom: 8px;
  left: 8px;
  z-index: 2;
  display: flex;
  align-items: center;
  isolation: isolate;
  padding-right: 14px;
}

.summary-card__role-avatar {
  position: relative;
  display: block;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 0;
  appearance: none;
  overflow: hidden;
  border-radius: 50%;
  background: var(--profile-cover-avatar-bg);
  box-shadow: 0 0 8px rgb(0 0 0 / 0.24);
  cursor: pointer;
  transition:
    transform var(--app-interaction-duration) var(--app-interaction-ease),
    box-shadow 0.16s ease,
    filter 0.16s ease;
}

.summary-card__role-avatar + .summary-card__role-avatar {
  margin-left: -2px;
  mask-image: radial-gradient(circle 14px at -9.8px 50%, transparent 99%, white 100%);
  -webkit-mask-image: radial-gradient(circle 14px at -9.8px 50%, transparent 99%, white 100%);
}

.summary-card__role-avatar img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.summary-card__role-avatar:hover,
.summary-card__role-avatar:focus-visible {
  z-index: 2;
  transform: scale(var(--app-interaction-object-scale-hover));
  box-shadow: 0 5px 14px rgb(0 0 0 / 0.22);
  filter: drop-shadow(0 4px 10px rgb(0 0 0 / 0.22));
}

.summary-card__role-avatar:active {
  transform: scale(var(--app-interaction-object-scale-active));
}

.summary-card__badge {
  position: absolute;
  right: 0;
  bottom: 0;
  z-index: 2;
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  color: var(--profile-on-cover);
  text-shadow: 0 1px 2px rgb(0 0 0 / 0.25);
}

.summary-card__badge.has-text {
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  white-space: nowrap;
}

.summary-card__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 8px 0;
}

.summary-card__title {
  display: -webkit-box;
  overflow: hidden;
  margin: 0;
  color: var(--profile-card-text);
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.summary-card__author {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  gap: 5px;
  align-self: flex-start;
  padding: 0;
  border: 0;
  appearance: none;
  background: transparent;
  color: var(--profile-card-muted);
  font: inherit;
  font-size: 12px;
  line-height: 16px;
  text-align: left;
  cursor: pointer;
  transition:
    color 0.16s ease,
    opacity 0.16s ease;
}

.summary-card__author:hover,
.summary-card__author:focus-visible {
  color: var(--profile-card-text);
}

.summary-card__author:active {
  opacity: 0.72;
}

.summary-card__author-avatar {
  width: 18px;
  height: 18px;
  flex: 0 0 auto;
  border-radius: 50%;
  object-fit: cover;
}

.summary-card__author span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-card__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 6px;
  color: var(--profile-card-subtle);
  font-size: 12px;
  line-height: 16px;
}

.summary-card__stats span + span::before {
  content: '·';
  margin-right: 6px;
}

@media (max-width: 640px) {
  .summary-card {
    padding: 7px 5px 14px;
  }

  .summary-card__cover {
    border-radius: 10px;
  }

  .summary-card__body {
    padding: 7px 2px 0;
  }

  .summary-card__stats {
    gap: 2px 4px;
  }

  .summary-card__stats span + span::before {
    margin-right: 4px;
  }
}
</style>
